import type { Density, FilterOperator, FilterValue, SortSpec } from './types'

/**
 * Jadval holati URL'da yashaydi (X1: «havola ulashiladi»).
 *
 * Format — references/tables.md:
 *   ?f[status]=active&f[created_at][gte]=2026-01-01&sort=-created_at&page=2
 *
 * Nima uchun URL: moderator hamkasbiga «mana shu 47 ta yozuv» deb havola
 * yuboradi va u AYNAN shu ko'rinishni ochadi. Holat komponent ichida qolsa
 * bu mumkin emas.
 */

const OP_TO_URL: Record<FilterOperator, string> = {
  contains: 'contains',
  equals: 'eq',
  startsWith: 'starts',
  gt: 'gt',
  lt: 'lt',
  between: 'between', // amalda gte+lte juftligiga yoyiladi
  in: 'in',
  is: 'is',
}

const URL_TO_OP: Record<string, FilterOperator> = {
  contains: 'contains',
  eq: 'equals',
  starts: 'startsWith',
  gt: 'gt',
  lt: 'lt',
  in: 'in',
  is: 'is',
}

export interface TableUrlState {
  filters: FilterValue[]
  sort: SortSpec[]
  page: number
  pageSize: number
  search: string
  density?: Density
  hidden: string[]
}

export const DEFAULT_PAGE_SIZE = 50
export const PAGE_SIZES = [50, 100, 200] as const

/** `?f[key][op]=v` → nomlangan bo'laklar. Mos kelmasa `null`. */
function parseFilterParam(name: string): { key: string; op?: string } | null {
  const m = /^f\[([^\]]+)\](?:\[([^\]]+)\])?$/.exec(name)
  if (!m || !m[1]) return null
  return m[2] ? { key: m[1], op: m[2] } : { key: m[1] }
}

export function decodeState(search: string, prefix = ''): TableUrlState {
  const p = new URLSearchParams(search)
  const k = (n: string) => (prefix ? `${prefix}_${n}` : n)

  // Bir kalit bo'yicha bir nechta parametr bo'lishi mumkin (gte + lte).
  const byKey = new Map<string, Map<string, string>>()
  for (const [name, value] of p.entries()) {
    const parsed = parseFilterParam(prefix ? name.replace(new RegExp(`^${prefix}_`), '') : name)
    if (!parsed) continue
    const slot = byKey.get(parsed.key) ?? new Map<string, string>()
    slot.set(parsed.op ?? 'default', value)
    byKey.set(parsed.key, slot)
  }

  const filters: FilterValue[] = []
  for (const [key, ops] of byKey) {
    const gte = ops.get('gte')
    const lte = ops.get('lte')
    if (gte !== undefined || lte !== undefined) {
      filters.push({ key, op: 'between', value: [gte ?? '', lte ?? ''] })
      continue
    }
    for (const [opName, raw] of ops) {
      const op = opName === 'default' ? 'contains' : URL_TO_OP[opName]
      if (!op) continue
      if (op === 'in') {
        filters.push({ key, op, value: raw.split(',').filter(Boolean) })
      } else if (op === 'is') {
        filters.push({ key, op, value: raw === 'true' })
      } else {
        filters.push({ key, op, value: raw })
      }
    }
  }

  const sortRaw = p.get(k('sort')) ?? ''
  const sort: SortSpec[] = sortRaw
    .split(',')
    .filter(Boolean)
    .map((s) => (s.startsWith('-') ? { key: s.slice(1), desc: true } : { key: s, desc: false }))

  const pageNum = Number(p.get(k('page')))
  const sizeNum = Number(p.get(k('size')))
  const densityRaw = p.get(k('d'))
  const density =
    densityRaw === 'compact' || densityRaw === 'normal' || densityRaw === 'comfortable'
      ? densityRaw
      : undefined

  return {
    filters,
    sort,
    page: Number.isFinite(pageNum) && pageNum > 0 ? pageNum : 1,
    pageSize: PAGE_SIZES.includes(sizeNum as (typeof PAGE_SIZES)[number]) ? sizeNum : DEFAULT_PAGE_SIZE,
    search: p.get(k('q')) ?? '',
    ...(density ? { density } : {}),
    hidden: (p.get(k('hide')) ?? '').split(',').filter(Boolean),
  }
}

export function encodeState(state: TableUrlState, prefix = ''): string {
  const p = new URLSearchParams()
  const k = (n: string) => (prefix ? `${prefix}_${n}` : n)
  const fk = (key: string, op?: string) => {
    const base = op ? `f[${key}][${op}]` : `f[${key}]`
    return prefix ? `${prefix}_${base}` : base
  }

  for (const f of state.filters) {
    if (f.op === 'between' && Array.isArray(f.value)) {
      const [from, to] = f.value
      if (from !== undefined && from !== '') p.set(fk(f.key, 'gte'), String(from))
      if (to !== undefined && to !== '') p.set(fk(f.key, 'lte'), String(to))
      continue
    }
    if (f.op === 'in' && Array.isArray(f.value)) {
      if (f.value.length) p.set(fk(f.key, 'in'), f.value.join(','))
      continue
    }
    // `contains` — matn ustunlarining standarti, URL'da qisqartiriladi.
    const opToken = f.op === 'contains' ? undefined : OP_TO_URL[f.op]
    p.set(fk(f.key, opToken), String(f.value))
  }

  if (state.sort.length) {
    p.set(k('sort'), state.sort.map((s) => (s.desc ? `-${s.key}` : s.key)).join(','))
  }
  if (state.page > 1) p.set(k('page'), String(state.page))
  if (state.pageSize !== DEFAULT_PAGE_SIZE) p.set(k('size'), String(state.pageSize))
  if (state.search) p.set(k('q'), state.search)
  if (state.density) p.set(k('d'), state.density)
  if (state.hidden.length) p.set(k('hide'), state.hidden.join(','))

  return p.toString()
}
